import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import { optInMfaRequest } from 'api/requests/auth-mfa-requests';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dismissMfaBanner, isMfaBannerDismissed } from 'services/mfa-banner-dismissal';
import localStorageService from 'services/local-storage-service';
import { useAppDispatch, useAppSelector } from 'store';
import { setAuthTokenAction } from 'store/actions/auth-actions';
import { fetchProfileAsync } from 'store/actions/profile-actions';
import { profileSelector } from 'store/selectors/profile-selectors';
import {
  GENERAL__ERROR_MESSAGE,
  MFA_ENROLLMENT__BANNER_CTA,
  MFA_ENROLLMENT__BANNER_DISMISS,
  MFA_ENROLLMENT__BANNER_MESSAGE,
  MFA_ENROLLMENT__BANNER_MESSAGE_NO_DATE,
} from 'translations/constants';
import IToken from 'types/auth/IToken';
import { formatMfaMandatoryDate } from 'utils/mfa-mandatory-date';
import MfaEnrollmentModal from './MfaEnrollmentModal';
import { MfaBannerCta, MfaBannerError, MfaBannerMessage, MfaBannerStyled } from './styles';

/** Ends the backend's 409 key, matched the way `mfa-helpers` matches its own: on the suffix. */
const ALREADY_ENROLLED_KEY = 'ALREADY_ENROLLED';

/**
 * Invites an enforced-but-not-yet-enrolled user to set up MFA before it becomes mandatory.
 *
 * `showMfaEnrollmentBanner` is the *only* condition. It is deliberately not re-derived by comparing
 * `mfaMandatoryFrom` to today: the backend also weighs enforcement and enrollment, neither of which
 * this client can see, and the date is populated on every response once configured — including for
 * users who are already enrolled and users MFA does not apply to. It is copy, not a condition.
 *
 * Read from `/Profile` rather than the login response because that is what survives: SSO,
 * impersonation, the company switch and a plain page reload all arrive with a bare token, and
 * `AppRouter` refetches the profile on every bootstrap.
 */
const MfaEnrollmentBanner = () => {
  const [t, i18n] = useTranslation();
  const dispatch = useAppDispatch();
  const profile = useAppSelector(profileSelector);

  const userId = profile?.id;
  // `typeof`, not truthiness: a falsy-but-real id would silently turn dismissal into a no-op.
  const hasUserId = typeof userId === 'number';
  // Lazily initialised as well as synced below: `useEffect` runs after paint, so seeding it there
  // alone would flash an already-dismissed banner on every reload.
  const [dismissed, setDismissed] = useState(() => hasUserId && isMfaBannerDismissed(userId));
  const [startingOptIn, setStartingOptIn] = useState(false);
  const [errorKey, setErrorKey] = useState('');
  // Non-null only while the opt-in dialog is open. Never persisted: it is a 15-minute pending-
  // enrollment token, not a session.
  const [mfaToken, setMfaToken] = useState<string>(null);

  // Re-read on every identity change: impersonating someone and stopping again swap who the profile
  // describes without necessarily remounting this component, and one user's dismissal must not
  // silence another's banner.
  useEffect(() => {
    setDismissed(typeof userId === 'number' && isMfaBannerDismissed(userId));
  }, [userId]);

  const onDismiss = useCallback(() => {
    setDismissed(true);
    if (typeof userId === 'number') {
      dismissMfaBanner(userId);
    }
  }, [userId]);

  const onSetUpNow = useCallback(async () => {
    setStartingOptIn(true);
    setErrorKey('');
    try {
      const { mfaToken: pendingToken } = await optInMfaRequest();
      setMfaToken(pendingToken);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      // 409 [MFA] ALREADY_ENROLLED — they enrolled in another tab. 403 [MFA] NOT_AUTHORIZED — MFA is
      // no longer enforced for them. Either way the flag this banner rendered on is stale, and
      // /Profile is the authority: refetching it turns the flag off and unmounts the banner.
      if (status === 409 || status === 403) {
        dispatch(fetchProfileAsync());
      } else {
        setErrorKey(GENERAL__ERROR_MESSAGE);
      }
    } finally {
      setStartingOptIn(false);
    }
  }, [dispatch]);

  const onCancelEnrollment = useCallback(() => setMfaToken(null), []);

  const onEnrollmentFailed = useCallback(
    (messageKey: string) => {
      setMfaToken(null);
      setErrorKey(messageKey);
      if (messageKey.endsWith(ALREADY_ENROLLED_KEY)) {
        dispatch(fetchProfileAsync());
      }
    },
    [dispatch],
  );

  /**
   * Confirming enrollment returns a brand-new session JWT. The old one still works, but only the new
   * one reflects the completed enrollment, so it replaces the stored token — under the impersonation
   * key when impersonating, which is the token `services/startup/load-auth` reads back first.
   *
   * The refetched profile is what actually takes the banner down: `showMfaEnrollmentBanner` comes
   * back false. Nothing navigates, so the user is left exactly where they were.
   */
  const onEnrolled = useCallback(
    (session: IToken) => {
      setMfaToken(null);
      if (localStorageService.getItem<boolean>('isImpersonated')) {
        localStorageService.setItem({ impersonationToken: session.token });
      } else {
        localStorageService.setItem({ authToken: session.token });
      }
      dispatch(setAuthTokenAction(session.token));
      dispatch(fetchProfileAsync());
    },
    [dispatch],
  );

  if (!profile?.showMfaEnrollmentBanner || dismissed) {
    return null;
  }

  const formattedDate = formatMfaMandatoryDate(profile.mfaMandatoryFrom, i18n.language);

  return (
    <>
      <MfaBannerStyled
        severity="warning"
        icon={false}
        action={
          <IconButton size="small" color="inherit" aria-label={t(MFA_ENROLLMENT__BANNER_DISMISS)} onClick={onDismiss}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        <MfaBannerMessage>
          {formattedDate
            ? t(MFA_ENROLLMENT__BANNER_MESSAGE, { date: formattedDate })
            : t(MFA_ENROLLMENT__BANNER_MESSAGE_NO_DATE)}
        </MfaBannerMessage>
        <MfaBannerCta text={t(MFA_ENROLLMENT__BANNER_CTA)} onClick={onSetUpNow} disabled={startingOptIn} />
        {errorKey && <MfaBannerError>{t(errorKey)}</MfaBannerError>}
      </MfaBannerStyled>
      {/* Unmounted between attempts on purpose: `useMfaEnrollment` latches its one Enroll call in a
          ref, and a fresh mount is what lets a second opt-in issue a fresh secret. */}
      {mfaToken && (
        <MfaEnrollmentModal
          mfaToken={mfaToken}
          onCancel={onCancelEnrollment}
          onFailed={onEnrollmentFailed}
          onEnrolled={onEnrolled}
        />
      )}
    </>
  );
};

export default MfaEnrollmentBanner;
